import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import {
    hasRowData,
    isCategoryHeaderRow,
    isCategoryItemRow,
    isContainedRow,
    isExpandableRow,
    isFullRowContent,
    isGroupHeaderRow,
    isSelectableRow,
    isStandardRow,
} from '../../base-table.helper';
import { GroupWithContentRows, InputRow } from '../../base-table.interface';
import { InputRowFilter, TableFilter, TableInputFilter, VisibleFilter } from './table-filter.interface';

type GroupedRowFilters<T, G, E> = {
    [groupId: string]: {
        [property in keyof Partial<T>]: TableInputFilter<T, G, E>[];
    };
};

/**
 * key to use for the "all parts" tab
 */
const ALL_TAB = '__all-parts__';

@Injectable({
    providedIn: null,
})
export class TableFilterService<T, G, E> implements OnDestroy {
    private currentFilters: GroupedRowFilters<T, G, E> = {};
    private currentGroup: GroupWithContentRows<G, T, E> | null = null;

    private activeFilters = new BehaviorSubject<TableInputFilter<T, G, E>[]>([]);
    private triggerInputUpdate = new Subject<void>();

    /**
     * Emit active filters whenever one of the filters or the group changed
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly filters = this.activeFilters.asObservable();

    /**
     * Emit active filters, but only when the group changed or a filter was removed
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly inputUpdate = this.triggerInputUpdate
        .asObservable()
        .pipe(switchMap(() => this.filters.pipe(take(1))));

    /**
     * Get ID for active group
     *
     * @param group Group to get ID for
     * @param allTab ID of "all" tab (used as fallback)
     * @returns ID of current group
     */
    public groupIdentifier: (group: GroupWithContentRows<G, T, E> | null, allTab: string) => string = (
        _,
        allTab
    ) => allTab;

    public ngOnDestroy(): void {
        this.activeFilters.complete();
        this.triggerInputUpdate.complete();
    }

    /**
     * Set a filter
     *
     * @param filterProp filter property
     * @param label filter label
     * @param value possible filter values
     * @param tagPosition tag position
     * @param isEqual when true, the search term must be present exactly. When false, the search term must be a substring of an item.
     */
    public setTextFilter(
        filterProp: keyof T & string,
        label: string,
        value: string,
        tagPosition: number,
        isEqual: boolean
    ) {
        this.setFilter(filterProp, label, value, this.getTextFilter(filterProp, value, isEqual), tagPosition);
    }

    public setMultiPropertyTextFilter(
        filterProps: ReadonlyArray<keyof T & string>,
        label: string,
        value: string,
        tagPosition: number,
        isEqual: boolean
    ) {
        const normalizedValue = value.toLowerCase();
        const filterFn: InputRowFilter<T, G, E> | null = !value
            ? null
            : (item: InputRow<T, G, E>) => {
                  if (isGroupHeaderRow(item) || isCategoryHeaderRow(item)) {
                      return true;
                  }

                  return filterProps.some((prop) => {
                      const itemValue = hasRowData(item) ? item.rowData[prop] : item[prop];
                      const normalizedItemValue = `${itemValue ?? ''}`.toLowerCase();

                      return isEqual
                          ? normalizedItemValue === normalizedValue
                          : normalizedItemValue.includes(normalizedValue);
                  });
              };

        this.setFilter('__global_search__' as keyof T & string, label, value, filterFn, tagPosition);
    }

    public setBoolFilter(filterProp: keyof T & string, label: string, value: boolean, tagPosition: number) {
        this.setFilter(filterProp, label, value, this.getBoolFilter(filterProp, value), tagPosition);
    }

    public updateGroupFilter(group?: GroupWithContentRows<G, T, E>) {
        this.currentGroup = group ?? null;
        this.updateActiveFilters(true);
    }

    public removeFilter(filter: TableFilter<InputRow<T, G, E>>) {
        if (filter.hide) {
            return;
        }

        const groupId = this.getGroupId();
        delete this.currentFilters[groupId][filter.property as keyof T];

        this.updateActiveFilters(true);
    }

    public watchPropertyValue(filterProp: keyof T & string): Observable<string | boolean> {
        return this.inputUpdate.pipe(
            map(
                (filters) =>
                    (
                        filters.find(
                            (filter) => !filter.hide && filter.property === filterProp
                        ) as VisibleFilter<InputRow<T, G, E>>
                    )?.value ?? ''
            )
        );
    }

    private getTextFilter(prop: keyof T, value: string, isEqual: boolean): InputRowFilter<T, G, E> | null {
        return !value
            ? null
            : (item: InputRow<T, G, E>) => {
                  if (isGroupHeaderRow(item) || isCategoryHeaderRow(item)) {
                      return true;
                  }

                  const itemValue =
                      isStandardRow(item) ||
                      isExpandableRow(item) ||
                      isSelectableRow(item) ||
                      isCategoryItemRow(item) ||
                      isFullRowContent(item) ||
                      isContainedRow(item)
                          ? item.rowData[prop]
                          : item[prop];

                  return isEqual
                      ? (itemValue as unknown as string)?.toLowerCase() === value.toLowerCase()
                      : ((itemValue as unknown as string)?.toLowerCase().includes(value.toLowerCase()) ??
                            false);
              };
    }

    private getBoolFilter(prop: keyof T, value: boolean): InputRowFilter<T, G, E> | null {
        return !value
            ? null
            : (item: InputRow<T, G, E>) => {
                  if (isGroupHeaderRow(item) || isCategoryHeaderRow(item)) {
                      return true;
                  }

                  const itemValue =
                      isStandardRow(item) ||
                      isExpandableRow(item) ||
                      isSelectableRow(item) ||
                      isCategoryItemRow(item) ||
                      isFullRowContent(item) ||
                      isContainedRow(item)
                          ? item.rowData[prop]
                          : item[prop];

                  return (itemValue as unknown as boolean) === value;
              };
    }

    private setFilter(
        filterProp: keyof T & string,
        label: string,
        value: string | boolean,
        filterFn: InputRowFilter<T, G, E> | InputRowFilter<T, G, E>[] | null,
        tagPosition: number
    ): void {
        const groupId = this.getGroupId();
        this.currentFilters[groupId] = this.currentFilters[groupId] || {};

        filterFn = filterFn ? (Array.isArray(filterFn) ? filterFn : [filterFn]) : [];
        this.currentFilters[groupId][filterProp] = filterFn.map((rowFilter) => ({
            label,
            property: filterProp,
            value,
            rowFilter,
            tagPosition,
        }));

        this.updateActiveFilters();
    }

    private updateActiveFilters(triggerInputUpdate = false) {
        const groupSelector = this.getGroupSelector(this.currentGroup);
        const groupFilters = this.getGroupFilters(this.currentGroup);
        const filters = [groupSelector, ...groupFilters]
            .filter<TableInputFilter<T, G, E>>((filter): filter is TableInputFilter<T, G, E> => !!filter)
            .sort(
                (filterA, filterB) =>
                    (filterA.hide ? -1 : filterA.tagPosition) - (filterB.hide ? -1 : filterB.tagPosition)
            );

        this.activeFilters.next(filters);

        if (triggerInputUpdate) {
            this.triggerInputUpdate.next();
        }
    }

    /**
     * Filter to apply to rows in order to only show those selected via their respective group tab
     */
    private getGroupSelector(group: GroupWithContentRows<G, T, E> | null): TableInputFilter<T, G, E> | null {
        return (
            group && {
                // create filter function that will return true ("display this row"), when …
                rowFilter: (row) =>
                    // … this row _is_ the selected group …
                    row === group ||
                    // … or this row is part of the selected group
                    (!isGroupHeaderRow(row) && group.containedRows.includes(row)),
                hide: true,
            }
        );
    }

    private getGroupFilters(group: GroupWithContentRows<G, T, E> | null): TableInputFilter<T, G, E>[] {
        const groupId = this.getGroupId(group);
        const groupFilters = this.currentFilters[groupId];

        return groupFilters
            ? Object.values<TableInputFilter<T, G, E>[]>(groupFilters).reduce(
                  (allFilters, propFilters) => [...allFilters, ...propFilters],
                  []
              )
            : [];
    }

    private getGroupId(group = this.currentGroup): string {
        return this.groupIdentifier(group, ALL_TAB);
    }
}
